import { useMemo } from 'react';
import { getVisitFeatureFlags, VisitFeatureFlags } from '@config/featureFlags';

export interface VisitClinicalSnapshot {
  status: string;
  startedAt?: string | null;
  cancelledAt?: string | null;
  veterinarianId?: string | null;
}

interface UserLike {
  id: string;
  role: string;
}

export interface VisitPermissions {
  canCheckIn: boolean;
  canEdit: boolean;
  canComplete: boolean;
  canSign: boolean;
  canReopen: boolean;
  canView: boolean;
  canAddAddendum: boolean;
  canStartVisit: boolean;
  editReason?: string;
}

/**
 * Permisiuni aliniate cu backend: draft + started_at → consult în desfășurare;
 * semnare doar vet/admin/master; recepție fără edit SOAP; addendum după semnare.
 */
export const useVisitPermissions = (
  clinical: VisitClinicalSnapshot | null,
  user: UserLike | null,
  flags?: VisitFeatureFlags
): VisitPermissions => {
  return useMemo(() => {
    const empty: VisitPermissions = {
      canCheckIn: false,
      canEdit: false,
      canComplete: false,
      canSign: false,
      canReopen: false,
      canView: true,
      canAddAddendum: false,
      canStartVisit: false,
      editReason: 'Nu există date despre vizită sau utilizator',
    };

    if (!clinical || !user) {
      return empty;
    }

    const featureFlags = flags || getVisitFeatureFlags();
    const role = (user.role || '').toLowerCase();
    const isReception = role === 'reception' || role === 'receptionist';
    const isVet = role === 'veterinarian' || role === 'doctor';
    const isClinicAdmin = role === 'clinic_admin' || role === 'admin';
    const isMaster = role === 'master_admin';
    const staffClinical = isVet || isClinicAdmin || isMaster;
    const staffAny = staffClinical || isReception;

    const status = (clinical.status || '').toLowerCase();
    const isDraftLike = status === 'draft' || status === 'in_progress';
    const isSignedLike = status === 'signed' || status === 'locked';
    const started = !!clinical.startedAt;
    const cancelled = !!clinical.cancelledAt;

    const assignedOk =
      !clinical.veterinarianId || clinical.veterinarianId === user.id || isClinicAdmin || isMaster;

    let canEdit = false;
    let editReason: string | undefined;

    if (cancelled) {
      editReason = 'Vizita este anulată.';
    } else if (isSignedLike) {
      editReason = 'Consultația este finalizată; folosiți addendum auditabil.';
    } else if (isReception) {
      editReason = 'Recepția nu poate modifica conținutul medical (politică clinică).';
    } else if (isDraftLike && staffClinical) {
      canEdit = true;
    } else if (!isDraftLike) {
      editReason = 'Status vizită nepermis pentru editare.';
    }

    const canStartVisit =
      staffAny &&
      isDraftLike &&
      !started &&
      !cancelled &&
      !isSignedLike;

    const canSign =
      staffClinical &&
      assignedOk &&
      isDraftLike &&
      started &&
      !cancelled &&
      !isSignedLike;

    const canAddAddendum = staffClinical && isSignedLike;

    return {
      canView: true,
      canCheckIn: isReception || staffClinical,
      canEdit,
      canComplete: false,
      canSign,
      canReopen: false,
      canAddAddendum,
      canStartVisit,
      editReason: canEdit ? undefined : editReason,
    };
  }, [clinical, user, flags]);
};
